import { DESKTOP_GAME_SOURCE_CONTRACT } from "./sourceContract";

export type CubeQuaternion = readonly [number, number, number, number];

export interface CanonicalGameFrame {
  readonly elapsed: number;
  readonly entry: number;
  readonly exit: number;
  readonly inside: boolean;
  readonly slowMotion: boolean;
  readonly motionScale: number;
  readonly braking: boolean;
  readonly stopped: boolean;
  readonly grounded: boolean;
  readonly tunnelOffset: number;
  readonly cubeX: number;
  readonly cubeY: number;
  readonly cubeZ: number;
  readonly cubeQuaternion: CubeQuaternion;
  readonly showcaseComplete?: boolean;
}

interface Quaternion { x: number; y: number; z: number; w: number; }

interface RollAction {
  elapsed: number;
  readonly startX: number;
  readonly endX: number;
  readonly start: Quaternion;
  readonly end: Quaternion;
}

const P = DESKTOP_GAME_SOURCE_CONTRACT.physics;
const clamp = (value: number, min = 0, max = 1): number => Math.max(min, Math.min(max, value));
const smooth = (value: number): number => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};
export const CANONICAL_GAME_ENTRY = {
  approachSeconds: 1,
  durationSeconds: 1.8,
  fallVelocity: -5.5,
  handoffProgress: smooth(1 / 1.8),
} as const;
const SHOWCASE_GAME_ENTRY={...CANONICAL_GAME_ENTRY,approachSeconds:1.3,durationSeconds:2.1,handoffProgress:smooth(1.3/2.1)};
const normalized = (q: Quaternion): Quaternion => {
  const length = Math.hypot(q.x, q.y, q.z, q.w) || 1;
  return { x: q.x / length, y: q.y / length, z: q.z / length, w: q.w / length };
};
const axisAngle = (x: number, y: number, z: number, angle: number): Quaternion => {
  const half = angle * 0.5;
  const sine = Math.sin(half);
  return { x: x * sine, y: y * sine, z: z * sine, w: Math.cos(half) };
};
const multiplied = (left: Quaternion, right: Quaternion): Quaternion => normalized({
  x: left.w * right.x + left.x * right.w + left.y * right.z - left.z * right.y,
  y: left.w * right.y - left.x * right.z + left.y * right.w + left.z * right.x,
  z: left.w * right.z + left.x * right.y - left.y * right.x + left.z * right.w,
  w: left.w * right.w - left.x * right.x - left.y * right.y - left.z * right.z,
});
const slerped = (from: Quaternion, toInput: Quaternion, amount: number): Quaternion => {
  const t = clamp(amount);
  let to = toInput;
  let dot = from.x * to.x + from.y * to.y + from.z * to.z + from.w * to.w;
  if (dot < 0) {
    dot = -dot;
    to = { x: -to.x, y: -to.y, z: -to.z, w: -to.w };
  }
  if (dot > 0.9995) return normalized({
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    z: from.z + (to.z - from.z) * t,
    w: from.w + (to.w - from.w) * t,
  });
  const theta = Math.acos(clamp(dot, -1, 1));
  const denominator = Math.sin(theta) || 1;
  const a = Math.sin((1 - t) * theta) / denominator;
  const b = Math.sin(t * theta) / denominator;
  return normalized({
    x: from.x * a + to.x * b,
    y: from.y * a + to.y * b,
    z: from.z * a + to.z * b,
    w: from.w * a + to.w * b,
  });
};

/** Desktop snapCubeQuaternionToFace: Three's XYZ Euler extraction, followed
 * by quarter-turn rounding and Quaternion.setFromEuler(...).normalize(). */
const faceSnapped = (q: Quaternion): Quaternion => {
  const m11 = 1 - 2 * (q.y * q.y + q.z * q.z);
  const m12 = 2 * (q.x * q.y - q.w * q.z);
  const m13 = 2 * (q.x * q.z + q.w * q.y);
  const m22 = 1 - 2 * (q.x * q.x + q.z * q.z);
  const m23 = 2 * (q.y * q.z - q.w * q.x);
  const m32 = 2 * (q.y * q.z + q.w * q.x);
  const m33 = 1 - 2 * (q.x * q.x + q.y * q.y);
  let x: number;
  let y = Math.asin(clamp(m13, -1, 1));
  let z: number;
  if (Math.abs(m13) < 0.9999999) {
    x = Math.atan2(-m23, m33);
    z = Math.atan2(-m12, m11);
  } else {
    x = Math.atan2(m32, m22);
    z = 0;
  }
  const quarter = Math.PI / 2;
  x = Math.round(x / quarter) * quarter;
  y = Math.round(y / quarter) * quarter;
  z = Math.round(z / quarter) * quarter;
  const c1 = Math.cos(x / 2), c2 = Math.cos(y / 2), c3 = Math.cos(z / 2);
  const s1 = Math.sin(x / 2), s2 = Math.sin(y / 2), s3 = Math.sin(z / 2);
  return normalized({
    x: s1 * c2 * c3 + c1 * s2 * s3,
    y: c1 * s2 * c3 - s1 * c2 * s3,
    z: c1 * c2 * s3 + s1 * s2 * c3,
    w: c1 * c2 * c3 - s1 * s2 * s3,
  });
};

type ActionKind = "left" | "right" | "jump" | "double" | "triple" | "slow" | "slowJump" | "airLeft" | "airRight" | "slowDouble";
const ACTION_KINDS: readonly ActionKind[] = ["left", "right", "jump", "double", "triple", "slow", "slowJump"];
const SHOWCASE_ACTIONS: readonly ActionKind[] = ['jump','double'];
const ENCORE_ACTIONS: readonly ActionKind[] = ['left','airLeft','double','right','slowDouble','airRight','left','slowJump','right','triple'];
const DEFAULT_SEED = 0x42424944;
const BRAKE_END = 0.55;
const approach = (from: number, to: number, dt: number, rate: number): number => {
  const next = from + (to - from) * (1 - Math.exp(-rate * dt));
  return Math.abs(next - to) < 0.001 ? to : next;
};

/**
 * A seeded input driver, not a replacement physics model. Each scheduled
 * action is the equivalent of a Desktop key press. The website alone owns
 * the entry/exit direction and the scroll-controlled time scale at its edge.
 */
export class CanonicalGameSimulation {
  private lastNow = 0;
  private elapsed = 0;
  private actionTime = 0;
  private nextActionAt = 1.6;
  private actionBag: ActionKind[] = [];
  private randomState = DEFAULT_SEED;
  private pendingJumps: number[] = [];
  private pendingSide: {at:number;direction:-1|1}|null=null;
  private slowStart = Infinity;
  private slowEnd = -Infinity;
  private exitIntent = 0;
  private brakeProgress = 0;
  private exitProgress = 0;
  private worldOffset = 0;
  private x = 0;
  private targetX = 0;
  private y = 2.45;
  private jumpVelocity: number = CANONICAL_GAME_ENTRY.fallVelocity;
  private jumpCount = 0;
  private quaternion: Quaternion = { x: 0, y: 0, z: 0, w: 1 };
  private roll: RollAction | null = null;
  private showcaseIndex = 0;
  private showcaseComplete = false;
  private exitSeconds = 0;
  private reverseExit = false;

  private get entrySettings(){return this.mode==='showcase'?SHOWCASE_GAME_ENTRY:CANONICAL_GAME_ENTRY;}
  constructor(private readonly seed = DEFAULT_SEED, private readonly mode: 'endless'|'showcase' = 'endless') {
    this.randomState = seed >>> 0 || DEFAULT_SEED;
    if(mode==='showcase')this.quaternion=axisAngle(1,0,0,Math.PI/2);
  }

  reset(now = 0): void {
    this.lastNow = now;
    this.elapsed = 0;
    this.actionTime = 0;
    this.nextActionAt = 1.6;
    this.actionBag = [];
    this.randomState = this.seed >>> 0 || DEFAULT_SEED;
    this.pendingJumps = [];
    this.pendingSide = null;
    this.slowStart = Infinity;
    this.slowEnd = -Infinity;
    this.exitIntent = 0;
    this.brakeProgress = 0;
    this.exitProgress = 0;
    this.worldOffset = 0;
    this.x = 0;
    this.targetX = 0;
    this.y = 2.45;
    this.jumpVelocity = CANONICAL_GAME_ENTRY.fallVelocity;
    this.jumpCount = 0;
    this.quaternion = this.mode==='showcase'?axisAngle(1,0,0,Math.PI/2):{x:0,y:0,z:0,w:1};
    this.roll = null;
    this.showcaseIndex=0;this.showcaseComplete=false;this.exitSeconds=0;
  }

  /** 0 keeps the simulation alive indefinitely. 0..0.55 brakes; 0.55..1
   * requests the outward jump, which cannot start before a complete stop. */
  advance(now: number, exitIntent = 0, reverseExit = false): CanonicalGameFrame {
    // Match Desktop animate(): one semi-implicit step per frame, capped at
    // 1/30 s after a stall. Catch-up substeps change both jump height and roll
    // timing and must not be used for this source-faithful replay.
    const dt = Math.min(1 / 30, this.lastNow ? Math.max(0, (now - this.lastNow) / 1000) : 0.016);
    this.lastNow = now;
    this.exitIntent = Number.isFinite(exitIntent) ? clamp(exitIntent) : 0;
    this.reverseExit=reverseExit&&this.exitIntent===0;
    this.step(dt);
    return this.frame();
  }

  private step(dt: number): void {
    this.elapsed += dt;
    this.updateExit(dt);
    const afterHandoff = Math.min(dt, Math.max(0, this.elapsed - this.entrySettings.approachSeconds));
    const physicsDt = afterHandoff * this.motionScale();
    if (physicsDt <= 0) return;

    // Pause the input clock while the visitor is leaving. Reversing scroll
    // cannot dispatch a backlog of simultaneous jump impulses.
    if (this.exitIntent === 0 && this.brakeProgress === 0) {
      this.actionTime += physicsDt;
      this.updateActions();
    }

    const slow = this.isSlowMotion();
    const speed = P.worldSpeed * (slow ? P.brakeMultiplier : 1);
    this.worldOffset = (this.worldOffset + speed * physicsDt) % DESKTOP_GAME_SOURCE_CONTRACT.tunnelGeometry.cell;
    this.updateJump(physicsDt, slow);
    if (this.roll) this.updateRoll(physicsDt);
    else this.quaternion = multiplied(axisAngle(1, 0, 0, (-speed * physicsDt) / P.cubeSize), this.quaternion);
  }

  private updateExit(dt: number): void {
    // A departure first cancels future input, not gravity. Finish an airborne
    // action and its side step before slowing the common cube/grid clock.
    const resting = this.y === 0 && !this.roll;
    const retired = this.mode==='showcase' && this.elapsed>=300;
    if(this.exitIntent>0 || retired){this.pendingJumps=[];this.pendingSide=null;}
    const wantsBrake = this.exitProgress > 0 || retired ? 1 : clamp(this.exitIntent / BRAKE_END);
    const targetBrake = resting || this.brakeProgress>0 ? wantsBrake : 0;
    this.brakeProgress = approach(this.brakeProgress, targetBrake, dt, 5);
    if (this.brakeProgress === 1) {
      // Input asks for take-off; it cannot drag an airborne body along a curve
      // or restart its clock when a partial request finally reaches one.
      if(this.exitIntent>BRAKE_END||this.exitSeconds>0){
        this.exitSeconds=Math.max(0,this.exitSeconds+(this.reverseExit?-dt:dt));this.exitProgress=clamp(this.exitSeconds/1.25);
      }
    }
  }

  private motionScale(): number {
    return 1 - smooth(this.brakeProgress);
  }

  private random(): number {
    let value = this.randomState;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.randomState = value >>> 0;
    return this.randomState / 0x100000000;
  }

  private nextKind(): ActionKind {
    if (!this.actionBag.length) {
      this.actionBag = [...(this.mode==='showcase'?ENCORE_ACTIONS:ACTION_KINDS)];
      for (let index = this.actionBag.length - 1; index > 0; index -= 1) {
        const other = Math.floor(this.random() * (index + 1));
        [this.actionBag[index], this.actionBag[other]] = [this.actionBag[other]!, this.actionBag[index]!];
      }
    }
    return this.actionBag.pop()!;
  }

  private updateActions(): void {
    if(this.pendingSide && this.pendingSide.at<=this.actionTime){this.startSide(this.pendingSide.direction);this.pendingSide=null;}
    if (this.pendingJumps.length && this.pendingJumps[0]! <= this.actionTime) {
      this.pendingJumps.shift();
      this.jump();
    }
    if (this.actionTime < this.nextActionAt || this.y > 0 || this.roll || this.pendingJumps.length) return;

    if(this.mode==='showcase'&&this.elapsed>=300)return;
    if(this.mode==='showcase'&&!this.showcaseComplete&&this.showcaseIndex>=SHOWCASE_ACTIONS.length){this.showcaseComplete=true;this.nextActionAt=this.actionTime+2.3;return;}
    const mandatory=this.mode==='showcase'&&!this.showcaseComplete;
    const kind = mandatory?SHOWCASE_ACTIONS[this.showcaseIndex++]!:this.nextKind();
    let actionDuration: number = P.rollDuration;
    if (kind === "left" || kind === "right") {
      // The driver steers back toward centre; the source wall limits and
      // exact one-cube-wide key press remain unchanged.
      const direction = Math.abs(this.targetX) >= P.sideStep * 2
        ? this.targetX > 0 ? -1 : 1
        : kind === "left" ? -1 : 1;
      this.startSide(direction);
    } else if (kind === "slow") {
      this.slowStart = this.actionTime;
      this.slowEnd = this.actionTime + 1.25 + this.random() * 0.55;
      actionDuration = this.slowEnd - this.actionTime;
    } else {
      this.jump();
      actionDuration = 1.3;
      if (kind === "double" || kind === "triple" || kind === 'slowDouble') {
        // Wait through the apex, then press again. Separate velocity resets
        // make a true mid-air second/third jump, not a larger initial impulse.
        const secondAt = this.actionTime + 0.68 + (this.mode==='showcase'?0:this.random() * 0.07);
        this.pendingJumps.push(secondAt);
        if (kind === "triple") this.pendingJumps.push(secondAt + 0.68 + this.random() * 0.07);
        actionDuration = (this.pendingJumps[this.pendingJumps.length - 1]! - this.actionTime) + 1.5;
        if(kind==='slowDouble'){this.slowStart=secondAt+.45;this.slowEnd=this.slowStart+1.3;actionDuration+=1.3;}
      } else if(kind==='airLeft'||kind==='airRight'){
        const direction=Math.abs(this.targetX)>=P.sideStep*3?(this.targetX>0?-1:1):kind==='airLeft'?-1:1;
        this.pendingSide={at:this.actionTime+.4,direction};
      } else if (kind === "slowJump") {
        // Down is pressed around the apex, as in Desktop: 0.42 world speed
        // and 4.4 gravity, not a scaled-down jump impulse.
        this.slowStart = this.actionTime + 0.58;
        this.slowEnd = this.slowStart + 1.1;
        actionDuration = 2.6;
      }
    }
    this.nextActionAt = this.actionTime + actionDuration + (mandatory?1.05:1.1 + this.random() * 2.1);
  }

  private jump(): void {
    if (this.jumpCount >= P.maxJumps) return;
    this.jumpVelocity = P.jumpImpulse;
    this.jumpCount += 1;
  }

  private updateJump(dt: number, slow: boolean): void {
    const softFall = slow && this.y > 0.02;
    this.jumpVelocity -= (softFall ? P.softFallGravity : P.gravity) * dt;
    this.y = Math.max(0, this.y + this.jumpVelocity * dt);
    if (this.y <= 0) {
      this.y = 0;
      this.jumpVelocity = 0;
      this.jumpCount = 0;
    }
  }

  private startSide(direction: -1 | 1): void {
    if (this.roll) this.finishRoll();
    const endX = clamp(this.targetX + direction * P.sideStep, -P.lateralLimit, P.lateralLimit);
    if (Math.abs(endX - this.targetX) < 0.0001) return;
    this.targetX = endX;
    this.quaternion = faceSnapped(this.quaternion);
    const start = { ...this.quaternion };
    const end = multiplied(axisAngle(0, 0, 1, -direction * Math.PI / 2), start);
    this.roll = { elapsed: 0, startX: this.x, endX, start, end };
  }

  private updateRoll(dt: number): void {
    const action = this.roll;
    if (!action) return;
    action.elapsed += dt;
    const progress = smooth(action.elapsed / P.rollDuration);
    this.x = action.startX + (action.endX - action.startX) * progress;
    this.quaternion = slerped(action.start, action.end, progress);
    if (action.elapsed >= P.rollDuration) this.finishRoll();
  }

  private finishRoll(): void {
    const action = this.roll;
    if (!action) return;
    this.x = action.endX;
    this.targetX = action.endX;
    this.quaternion = faceSnapped(action.end);
    this.roll = null;
  }

  private rollLift(): number {
    if (!this.roll) return 0;
    const progress = smooth(this.roll.elapsed / P.rollDuration);
    return Math.sin(progress * Math.PI) * P.cubeRadius * 0.18;
  }

  private isSlowMotion(): boolean {
    return this.actionTime >= this.slowStart && this.actionTime < this.slowEnd;
  }

  private frame(): CanonicalGameFrame {
    const entry = smooth(this.elapsed / this.entrySettings.durationSeconds);
    const exit = this.exitProgress;
    return {
      elapsed: this.elapsed,
      entry,
      exit,
      inside: this.elapsed >= this.entrySettings.approachSeconds && exit < 1,
      slowMotion: this.isSlowMotion(),
      motionScale: this.motionScale(),
      braking: this.brakeProgress > 0,
      stopped: this.brakeProgress === 1,
      grounded: this.y === 0 && !this.roll,
      tunnelOffset: this.worldOffset,
      cubeX: this.x,
      cubeY: this.y + this.rollLift(),
      cubeZ: P.cubeZ + exit * 8.45,
      cubeQuaternion: [this.quaternion.x, this.quaternion.y, this.quaternion.z, this.quaternion.w],
      showcaseComplete:this.showcaseComplete,
    };
  }
}
